import { useEffect, useMemo, useState } from "react";
import { Minus, Plus, X } from "lucide-react";
import { SAR } from "./RiyalIcon";
import { absoluteUrl } from "./ImageUpload";
import ProductQuantityControl from "./ProductQuantityControl";
import { triggerLightHaptic } from "../lib/haptics";

const selectedUnits = (selection) => (
  Object.values(selection || {}).reduce((total, quantity) => total + Number(quantity || 0), 0)
);

const initialSingleOptionSelections = (groups) => (
  groups.reduce((selection, group) => {
    const options = group.options || [];
    const minimum = Number(group.min || 0);
    const maximum = group.max === null ? null : Number(group.max);

    if (options.length !== 1 || minimum <= 0) {
      return selection;
    }

    const quantity = maximum === null ? minimum : Math.min(minimum, maximum);
    if (quantity > 0) {
      selection[group.id] = { [options[0].id]: quantity };
    }

    return selection;
  }, {})
);

function OptionPriceDelta({ value }) {
  const amount = Number(value || 0);

  if (amount === 0) {
    return null;
  }

  return (
    <span className="text-sm font-bold text-black">
      <SAR>{amount}</SAR>
    </span>
  );
}

/**
 * Bottom sheet to customize a product with option groups.
 * onAdd(cartLine) where cartLine contains the selected add-ons and their price total.
 */
export default function ProductSheet({ product, onClose, onAdd }) {
  const groups = useMemo(() => product.option_groups || [], [product.option_groups]);
  const [qty, setQty] = useState(1);
  const [selected, setSelected] = useState(() => initialSingleOptionSelections(groups));
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const selectSingleOption = (group, optionId) => {
    setSelected((current) => {
      const currentQuantity = Number(current[group.id]?.[optionId] || 0);
      const nextSelection = !group.required && currentQuantity === 1
        ? {}
        : { [optionId]: 1 };

      return { ...current, [group.id]: nextSelection };
    });
  };

  const changeOptionQuantity = (group, optionId, change) => {
    setSelected((current) => {
      const groupSelection = current[group.id] || {};
      const currentQuantity = Number(groupSelection[optionId] || 0);
      const groupTotal = selectedUnits(groupSelection);

      if (change > 0 && group.max !== null && groupTotal >= Number(group.max)) return current;
      if (change < 0 && currentQuantity === 0) return current;

      const nextSelection = { ...groupSelection };
      const nextQuantity = currentQuantity + change;
      if (nextQuantity <= 0) delete nextSelection[optionId];
      else nextSelection[optionId] = nextQuantity;

      return { ...current, [group.id]: nextSelection };
    });
  };

  const { unitExtra, snapshot, canAdd, missing } = useMemo(() => {
    let extra = 0; const snap = []; let miss = null;
    for (const g of groups) {
      const groupSelection = selected[g.id] || {};
      const groupTotal = selectedUnits(groupSelection);
      if (g.required && groupTotal < Math.max(1, g.min || 1)) miss = g.name;
      if (groupTotal < (g.min || 0)) miss = g.name;
      for (const option of g.options || []) {
        const optionQuantity = Number(groupSelection[option.id] || 0);
        for (let index = 0; index < optionQuantity; index += 1) {
          extra += Number(option.price_delta || 0);
          snap.push({
            group_id: g.id,
            group_name: g.name,
            option_id: option.id,
            name: option.name,
            price_delta: Number(option.price_delta || 0),
          });
        }
      }
    }
    return { unitExtra: extra, snapshot: snap, canAdd: !miss, missing: miss };
  }, [selected, groups]);

  const unitPrice = Number(product.price) + unitExtra;
  const total = unitPrice * qty;

  const closeAfterAnimation = (callback) => {
    if (isClosing) return;
    setIsClosing(true);
    window.setTimeout(callback, 180);
  };

  const close = () => closeAfterAnimation(onClose);

  const add = () => {
    if (!canAdd) return;
    closeAfterAnimation(() => {
      onAdd({
        product,
        quantity: qty,
        selected_addons: snapshot.map(s => ({ group_id: s.group_id, option_id: s.option_id })),
        addon_snapshot: snapshot,
        unit_extra: unitExtra,
      });
    });
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center bg-black/50 sm:items-center"
      onClick={close}
    >
      <div
        onClick={e => e.stopPropagation()}
        className={
          "w-full h-[100dvh] sm:h-auto sm:max-w-md bg-white sm:rounded-3xl max-h-[100dvh] "
          + "sm:max-h-[92dvh] relative overflow-hidden flex flex-col "
          + (isClosing ? "product-sheet-exit" : "product-sheet-enter")
        }
        data-testid="product-sheet"
      >
        <button
          type="button"
          onClick={close}
          className={
            "absolute top-[max(0.75rem,env(safe-area-inset-top))] start-3 z-10 w-10 h-10 "
            + "rounded-full bg-white/90 grid place-items-center"
          }
          data-testid="sheet-close"
        >
          <X size={18} />
        </button>
        <div
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
          data-testid="product-sheet-content"
        >
          {product.image_url ? (
            <div className="h-48 food-img overflow-hidden">
              <img
                src={absoluteUrl(product.image_url)}
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
          ) : <div className="h-24 brand-soft" />}
          <div className="p-5 pb-32">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-2xl font-extrabold">{product.name}</h2>
              <div className="mt-1 text-sm text-gray-600" data-testid="sheet-product-description">
                {product.description?.trim() || product.name}
              </div>
              <div className="mt-1 pt-1 text-xl font-bold text-black"><SAR>{product.price}</SAR></div>
            </div>
          </div>

          {groups.map((g) => {
            const groupSelection = selected[g.id] || {};
            const groupTotal = selectedUnits(groupSelection);

            return (
              <section key={g.id} className="mt-5" data-testid={`sheet-group-${g.id}`}>
              <div className="flex items-baseline justify-between mb-2">
                <div>
                  <span className="font-extrabold">{g.name}</span>
                  {g.required && <span className="text-red-500 me-1">*</span>}
                </div>
                <span className="text-xs text-gray-500">
                  {g.max === 1
                    ? "اختر خياراً واحداً"
                    : g.max === null ? "بلا حد أقصى" : `اختر حتى ${g.max}`}
                </span>
              </div>
              <div className="space-y-2">
                {(g.options || []).map((o) => {
                  const optionQuantity = Number(groupSelection[o.id] || 0);
                  const chosen = optionQuantity > 0;

                  if (g.max === 1) {
                    return (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => selectSingleOption(g, o.id)}
                      data-testid={`opt-${g.id}-${o.id}`}
                      className={
                        "w-full flex items-center justify-between rounded-2xl border-2 px-4 py-3 "
                        + `text-start ${chosen ? "border-orange-500 bg-orange-50" : "border-gray-200 bg-white"}`
                      }
                    >
                      <span className="flex items-center gap-2">
                        <span className={`w-5 h-5 grid place-items-center ${g.max === 1 ? "rounded-full" : "rounded-md"} border-2 ${chosen ? "border-orange-500 bg-orange-500 text-white" : "border-gray-300"}`}>
                          {chosen ? "✓" : ""}
                        </span>
                        <span className="font-bold">{o.name}</span>
                      </span>
                      <OptionPriceDelta value={o.price_delta} />
                    </button>
                    );
                  }

                  const atMaximum = g.max !== null && groupTotal >= Number(g.max);
                  return (
                    <div
                      key={o.id}
                      className={
                        "w-full flex items-center justify-between rounded-2xl border-2 px-4 py-3 "
                        + `text-start ${chosen ? "border-orange-500 bg-orange-50" : "border-gray-200 bg-white"}`
                      }
                    >
                      <span className="font-bold">{o.name}</span>
                      <span className="flex items-center gap-2">
                        <OptionPriceDelta value={o.price_delta} />
                        <ProductQuantityControl
                          quantity={optionQuantity}
                          onDecrease={() => changeOptionQuantity(g, o.id, -1)}
                          onIncrease={() => changeOptionQuantity(g, o.id, 1)}
                          increaseDisabled={atMaximum}
                          variant="sheet-option"
                          containerTestId={`opt-quantity-controls-${g.id}-${o.id}`}
                          decreaseTestId={`opt-dec-${g.id}-${o.id}`}
                          increaseTestId={
                            optionQuantity === 0
                              ? `opt-${g.id}-${o.id}`
                              : `opt-inc-${g.id}-${o.id}`
                          }
                          quantityTestId={`opt-qty-${g.id}-${o.id}`}
                          deleteIconTestId={`opt-delete-icon-${g.id}-${o.id}`}
                          minusIconTestId={`opt-minus-icon-${g.id}-${o.id}`}
                          increaseLabel={`إضافة ${o.name}`}
                          decreaseLabel={`تقليل ${o.name}`}
                          plusStrokeWidth={2.5}
                        />
                      </span>
                    </div>
                  );
                })}
              </div>
              </section>
            );
          })}

          </div>
        </div>

        <div className="shrink-0 bg-white border-t border-orange-100 p-4">
          {!canAdd && <div className="text-xs text-red-600 mb-2 text-center">يرجى اختيار «{missing}»</div>}
          <div className="flex items-center gap-2">
            <div className="basis-[30%] shrink-0">
              <SheetQuantityControl
                quantity={qty}
                onDecrease={() => setQty((current) => Math.max(1, current - 1))}
                onIncrease={() => setQty((current) => current + 1)}
              />
            </div>
            <button
              disabled={!canAdd}
              onClick={add}
              className={
                "flex-1 pill brand-grad text-white font-bold text-base py-3.5 tap-target "
                + "disabled:opacity-60 flex items-center justify-center gap-2"
              }
              data-testid="sheet-add"
            >
              إضافة إلى السلة · <SAR>{total}</SAR>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SheetQuantityControl({ quantity, onDecrease, onIncrease }) {
  const handleDecrease = (event) => {
    triggerLightHaptic();
    onDecrease(event);
  };

  const handleIncrease = (event) => {
    triggerLightHaptic();
    onIncrease(event);
  };

  return (
    <div
      className="grid h-[3.25rem] w-full grid-cols-[1fr_0.72fr_1fr] overflow-hidden rounded-xl border border-orange-200 bg-white"
      data-testid="sheet-quantity-controls"
      dir="ltr"
    >
      <button
        type="button"
        onClick={handleDecrease}
        className="grid place-items-center text-orange-700"
        data-testid="sheet-dec"
        aria-label="تقليل الكمية"
      >
        <Minus size={17} data-testid="sheet-minus-icon" />
      </button>
      <span
        className="grid place-items-center border-x border-orange-100 num-badge font-extrabold text-orange-800"
        data-testid="sheet-quantity"
      >
        {quantity}
      </span>
      <button
        type="button"
        onClick={handleIncrease}
        className="brand-grad grid place-items-center text-white"
        data-testid="sheet-inc"
        aria-label="زيادة الكمية"
      >
        <Plus size={18} />
      </button>
    </div>
  );
}
